import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Download, Loader2, Search, Table2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { AiDatasetRow, AiTurnDataset } from "../../../models/AiAssistant";
import { useAiDataset, useFetchAiDataset } from "../../../networking/ai-assistant.hooks";
import { downloadText, toCsv } from "../ReportBuilder/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { toEntityMarker, toInAppPath } from "./AiMarkdown";

/** Rows shown inline in the panel; the rest live in the dialog. */
export const PREVIEW_ROWS = 5;

interface AiDataTableProps {
	dataset: AiTurnDataset;
	/**
	 * The conversation the table belongs to. The turn carries a preview of the
	 * rows; the rest are fetched from the conversation's dataset endpoint when
	 * the table is opened or exported. Without it the table shows what it has.
	 */
	conversationId?: string;
	className?: string;
}

/**
 * The table behind an answer. The assistant is told to summarise lists
 * instead of repeating them — it never writes more than a couple of rows —
 * so every row the tools returned is rendered here: a short preview in the
 * panel, and the whole thing (searchable, sortable, exportable) in a dialog.
 */
const AiDataTable: React.FC<AiDataTableProps> = ({ dataset, conversationId, className }) => {
	const [open, setOpen] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [exportError, setExportError] = useState(false);
	const columns = useMemo(() => visibleColumns(dataset.columns), [dataset.columns]);
	const total = rowCountOf(dataset);
	const remote = !!conversationId && total > dataset.rows.length;
	const fetchDataset = useFetchAiDataset();

	const exportCsv = async () => {
		if (!remote) return downloadCsv(dataset);
		setExporting(true);
		setExportError(false);
		try {
			downloadCsv(await fetchDataset(conversationId as string, dataset.id));
		} catch {
			setExportError(true);
		} finally {
			setExporting(false);
		}
	};

	return (
		<div className={cn("rounded-md border border-border", className)}>
			<div className="flex items-center gap-1.5 border-b border-border bg-neutral-50 px-2 py-1.5 text-xs">
				<Table2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
				<span className="min-w-0 flex-1 truncate font-medium text-foreground">{dataset.title}</span>
				<span className="shrink-0 text-foreground-subtle">{rowSummary(dataset)}</span>
			</div>

			<div className="overflow-x-auto">
				<DataRows dataset={dataset} columns={columns} rows={dataset.rows.slice(0, PREVIEW_ROWS)} />
			</div>

			<div className="flex items-center gap-3 border-t border-border px-2 py-1.5 text-xs">
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
				>
					{total > PREVIEW_ROWS ? `Visa alla ${formatNumber(total)} rader` : "Visa i stort format"}
				</button>
				<button
					type="button"
					onClick={exportCsv}
					disabled={exporting}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-60"
					title="Ladda ner alla rader som CSV (öppnas i Excel)"
				>
					{exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CSV
				</button>
				{exportError && <span className="text-danger">Raderna kunde inte hämtas.</span>}
			</div>

			<AiDataTableDialog dataset={dataset} conversationId={conversationId} open={open} onOpenChange={setOpen} />
		</div>
	);
};

interface AiDataTableDialogProps {
	dataset: AiTurnDataset;
	conversationId?: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/** Every row of one dataset, on top of the app instead of in the 440px panel. */
export const AiDataTableDialog: React.FC<AiDataTableDialogProps> = ({
	dataset,
	conversationId,
	open,
	onOpenChange,
}) => {
	const [keyword, setKeyword] = useState("");
	const [sort, setSort] = useState<{ column: string; direction: "asc" | "desc" }>();
	const columns = useMemo(() => visibleColumns(dataset.columns), [dataset.columns]);
	const total = rowCountOf(dataset);
	const remote = !!conversationId && total > dataset.rows.length;
	const { dataset: fetched, isLoading, isError, refetch } = useAiDataset(conversationId, dataset.id, open && remote);
	const table = remote && fetched ? fetched : dataset;
	const loading = remote && !fetched && isLoading;

	const rows = useMemo(() => {
		const needle = keyword.trim().toLowerCase();
		const filtered = needle
			? table.rows.filter((row) =>
					columns.some((column) =>
						String(row[column] ?? "")
							.toLowerCase()
							.includes(needle)
					)
			  )
			: table.rows;

		if (!sort) return filtered;

		const factor = sort.direction === "asc" ? 1 : -1;
		return [...filtered].sort((a, b) => factor * compare(a[sort.column], b[sort.column]));
	}, [table.rows, columns, keyword, sort]);

	const toggleSort = (column: string) =>
		setSort((current) =>
			current?.column === column
				? current.direction === "asc"
					? { column, direction: "desc" }
					: undefined
				: { column, direction: "asc" }
		);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[85vh] w-[min(1100px,95vw)] max-w-none flex-col gap-3 p-4">
				<DialogHeader className="pr-8">
					<DialogTitle className="text-base">{dataset.title}</DialogTitle>
				</DialogHeader>

				<div className="flex flex-wrap items-center gap-2">
					<div className="relative">
						<Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
						<input
							value={keyword}
							onChange={(event) => setKeyword(event.target.value)}
							placeholder="Sök i tabellen"
							className="h-8 w-56 rounded-md border border-solid border-border pl-7 pr-2 text-xs outline-none focus:border-brand"
						/>
					</div>
					<span className="text-xs text-muted-foreground">
						{keyword.trim()
							? `${formatNumber(rows.length)} av ${formatNumber(table.rows.length)} rader`
							: rowSummary(dataset)}
					</span>
					<button
						type="button"
						onClick={() => downloadCsv(table)}
						disabled={loading || (remote && !fetched)}
						className="ml-auto inline-flex h-8 items-center gap-1 rounded-md border border-solid border-border px-2 text-xs text-foreground hover:bg-neutral-50 disabled:opacity-60"
						title="Ladda ner alla rader som CSV (öppnas i Excel)"
					>
						<Download className="h-3.5 w-3.5" /> Ladda ner CSV
					</button>
				</div>

				<div className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
					{loading ? (
						<p className="flex items-center gap-2 px-3 py-8 text-sm text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" /> Hämtar {formatNumber(total)} rader …
						</p>
					) : remote && isError && !fetched ? (
						<p className="px-3 py-8 text-sm text-muted-foreground">
							Raderna kunde inte hämtas.{" "}
							<button type="button" onClick={() => refetch()} className="font-medium text-blue-700 underline">
								Försök igen
							</button>
						</p>
					) : (
						<DataRows
							dataset={table}
							columns={columns}
							rows={rows}
							stickyHeader
							sort={sort}
							onSort={toggleSort}
						/>
					)}
				</div>

				{dataset.totalCount != null && dataset.totalCount > total && (
					<p className="text-xs text-muted-foreground">
						Tabellen rymmer {formatNumber(total)} av {formatNumber(dataset.totalCount)} rader. Be
						assistenten avgränsa frågan för att se resten.
					</p>
				)}
			</DialogContent>
		</Dialog>
	);
};

interface DataRowsProps {
	dataset: AiTurnDataset;
	columns: string[];
	rows: AiDatasetRow[];
	stickyHeader?: boolean;
	sort?: { column: string; direction: "asc" | "desc" };
	onSort?: (column: string) => void;
}

const DataRows: React.FC<DataRowsProps> = ({
	dataset,
	columns,
	rows,
	stickyHeader,
	sort,
	onSort,
}) => {
	const navigate = useNavigate();

	return (
		<table className="w-full border-collapse text-xs">
			<thead className={cn("bg-neutral-50", stickyHeader && "sticky top-0 z-10")}>
				<tr>
					{columns.map((column) => (
						<th
							key={column}
							onClick={onSort ? () => onSort(column) : undefined}
							className={cn(
								"whitespace-nowrap border-b border-border px-2 py-1.5 text-left font-semibold",
								onSort && "cursor-pointer select-none hover:bg-neutral-100"
							)}
						>
							<span className="inline-flex items-center gap-1">
								{columnLabel(column)}
								{sort?.column === column &&
									(sort.direction === "asc" ? (
										<ArrowUp className="h-3 w-3" />
									) : (
										<ArrowDown className="h-3 w-3" />
									))}
							</span>
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((row, index) => (
					<tr key={index} className="even:bg-neutral-50/60">
						{columns.map((column) => (
							<td
								key={column}
								className="max-w-[22rem] truncate border-b border-border px-2 py-1.5 align-top"
								title={plainText(row[column])}
							>
								{renderCell(row, column, navigate)}
							</td>
						))}
					</tr>
				))}
				{!rows.length && (
					<tr>
						<td
							colSpan={columns.length || 1}
							className="px-2 py-6 text-center text-muted-foreground"
						>
							Inga rader matchar sökningen.
						</td>
					</tr>
				)}
			</tbody>
		</table>
	);
};

function renderCell(
	row: AiDatasetRow,
	column: string,
	navigate: ReturnType<typeof useNavigate>
): React.ReactNode {
	const value = row[column];

	if (column === "url" && typeof value === "string") {
		const inApp = toInAppPath(value);
		return (
			<a
				href={value}
				target={inApp ? undefined : "_blank"}
				rel="noreferrer"
				onClick={(event) => {
					if (!inApp || event.metaKey || event.ctrlKey || event.shiftKey) return;
					event.preventDefault();
					navigate(inApp);
				}}
				className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
				{...toEntityMarker(value)}
			>
				Öppna
			</a>
		);
	}

	return plainText(value) || <span className="text-foreground-subtle">–</span>;
}

function plainText(value: unknown): string {
	if (value === null || value === undefined || value === "") return "";
	if (typeof value === "boolean") return value ? "Ja" : "Nej";
	if (typeof value === "number") return formatNumber(value);
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
}

function formatNumber(value: number): string {
	return Number.isInteger(value)
		? value.toLocaleString("sv-SE")
		: value.toLocaleString("sv-SE", { maximumFractionDigits: 2 });
}

/**
 * Raw ids are noise in a table (and the assistant is told not to show them),
 * but they stay in the export where they are what you paste into a system.
 */
export function visibleColumns(columns: string[]): string[] {
	const visible = columns.filter(
		(column) => !/(^|\.)(id|_id)$/i.test(column) && !/Id$/.test(column)
	);
	return visible.length ? visible : columns;
}

/**
 * Swedish headings for the fields the tools return most often. Anything else
 * (a SQL alias the model picked, a custom field) falls back to the field name
 * itself, which is why the fallback has to stay readable.
 */
const COLUMN_LABELS: Record<string, string> = {
	url: "Länk",
	name: "Namn",
	title: "Titel",
	status: "Status",
	active: "Aktiv",
	archived: "Arkiverad",
	email: "E-post",
	firstName: "Förnamn",
	lastName: "Efternamn",
	startDate: "Startdatum",
	endDate: "Slutdatum",
	startTime: "Starttid",
	endTime: "Sluttid",
	orderNumber: "Ordernummer",
	customerNumber: "Kundnummer",
	employeeNumber: "Anställningsnummer",
	phoneNumber: "Telefon",
	city: "Ort",
	pool: "Pool",
	tags: "Taggar",
	hours: "Timmar",
	noOfHours: "Antal timmar",
	reportedTime: "Rapporterad tid",
	invoicedTime: "Fakturerad tid",
	"customer.name": "Kund",
	"consultant.name": "Konsult",
	"contract.name": "Avtal",
	"metadata.created": "Skapad",
};

/** `customer.name` → "Kund", `noOfHours` → "Antal timmar", `foo_bar` → "Foo bar". */
export function columnLabel(column: string): string {
	const known = COLUMN_LABELS[column] ?? COLUMN_LABELS[column.split(".").pop() as string];
	if (known) return known;

	return column
		.split(".")
		.map((part) => humanize(part))
		.join(" · ");
}

function humanize(part: string): string {
	const words = part
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/_/g, " ")
		.split(" ");

	return words
		.map((word, index) => {
			if (word === word.toUpperCase()) return word;
			return index === 0 ? word.charAt(0).toUpperCase() + word.substring(1) : word.toLowerCase();
		})
		.join(" ");
}

/** Rows the table holds: `rowCount` when the turn carries a preview, else what it was given. */
function rowCountOf(dataset: AiTurnDataset): number {
	return Math.max(dataset.rowCount ?? 0, dataset.rows.length);
}

function rowSummary(dataset: AiTurnDataset): string {
	const held = rowCountOf(dataset);
	if (dataset.totalCount != null && dataset.totalCount > held)
		return `${formatNumber(held)} av ${formatNumber(dataset.totalCount)} rader`;
	return `${formatNumber(held)} ${held === 1 ? "rad" : "rader"}`;
}

function compare(a: unknown, b: unknown): number {
	if (a === b) return 0;
	if (a === null || a === undefined || a === "") return 1;
	if (b === null || b === undefined || b === "") return -1;
	if (typeof a === "number" && typeof b === "number") return a - b;
	return String(a).localeCompare(String(b), "sv");
}

function downloadCsv(dataset: AiTurnDataset) {
	const filename = `${
		dataset.title
			.toLowerCase()
			.replace(/[^\wåäö]+/g, "-")
			.replace(/^-|-$/g, "") || "tabell"
	}.csv`;
	downloadText(filename, toCsv(dataset.columns, dataset.rows as Record<string, unknown>[]));
}

export default AiDataTable;
